import React from "react";
import { Link } from "react-router-dom";
import { Flag, ArrowRight, Compass, ChartLineUp, Trophy } from "@phosphor-icons/react";
import { BulaLogo } from "@/components/BulaLogo";

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#F8F7F3] text-[#0F2537]">
      <header className="glass-header sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BulaLogo size={36} />
            <div className="leading-none">
              <div className="font-heading font-black text-lg">Bula Golf</div>
              <div className="label-eyebrow !text-[0.55rem]">Fiji Handicap Tracker</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/login" data-testid="landing-login-btn" className="px-3 py-2 text-sm font-semibold text-[#0F2537]/80 hover:text-[#0F2537]">Sign in</Link>
            <Link to="/register" data-testid="landing-register-btn" className="px-4 py-2 rounded-lg bg-[#0F2537] text-white text-sm font-semibold hover:bg-[#0F2537]/90 inline-flex items-center gap-1.5">
              Get started <ArrowRight size={14} weight="bold" />
            </Link>
          </div>
        </div>
      </header>

      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1538648759472-7251f7cb2c2f?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000"
            alt="" className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-[#0F2537]/85 via-[#0F2537]/70 to-[#0F2537]/40" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 md:px-8 py-20 md:py-32 text-white">
          <span className="inline-block label-eyebrow !text-white/70 mb-5 rise">FIJI · GOLF · COMMUNITY</span>
          <h1 className="font-heading font-black text-5xl sm:text-6xl lg:text-7xl tracking-tighter max-w-3xl leading-[0.95] rise rise-delay-1">
            Your handicap.<br />
            <span className="text-[#E05D3A]">Bula style.</span>
          </h1>
          <p className="mt-6 max-w-xl text-white/85 text-lg font-medium rise rise-delay-2">
            Record your rounds across Natadola Bay, Denarau, Pacific Harbour and every Fiji course. Get a true World Handicap System index — no spreadsheet required.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 rise rise-delay-3">
            <Link to="/register" data-testid="hero-cta-register" className="px-5 py-3 rounded-xl bg-[#E05D3A] text-white font-bold inline-flex items-center gap-2 hover:brightness-110 transition">
              Start tracking free <ArrowRight size={18} weight="bold" />
            </Link>
            <Link to="/login" data-testid="hero-cta-login" className="px-5 py-3 rounded-xl border border-white/30 text-white font-semibold hover:bg-white/10 transition">
              I have an account
            </Link>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-24">
        <div className="grid md:grid-cols-3 gap-5">
          {[
            { icon: Compass, title: "Every Fiji course", body: "Pre-loaded course data for Natadola Bay, Denarau, Pacific Harbour, Nadi Airport GC and Fiji Golf Club." },
            { icon: ChartLineUp, title: "Real WHS handicap", body: "Score differentials are calculated automatically. See your handicap index move with every card." },
            { icon: Trophy, title: "Friendly leaderboard", body: "See where you rank among Fiji golfers — by handicap, rounds played, scoring average." },
          ].map((f, i) => (
            <div key={i} className="card-default p-7 rise" style={{ animationDelay: `${i * 80}ms` }}>
              <f.icon size={28} weight="duotone" className="text-[#E05D3A]" />
              <h3 className="font-heading font-bold text-xl mt-4">{f.title}</h3>
              <p className="text-[#0F2537]/70 mt-2 text-[15px]">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing strip (Stripe-friendly: clear product + price + refund policy on the marketing site) */}
      <section id="pricing" className="bg-[#0F2537] text-white py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-5 md:px-8 text-center">
          <span className="label-eyebrow !text-white/70">Bula Pro</span>
          <h2 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter mt-2 max-w-2xl mx-auto">Unlock Shot Tracker, Club Bag & Strokes Gained.</h2>
          <p className="mt-4 text-white/80 max-w-xl mx-auto">All the core scoring + handicap features are free. Upgrade to Bula Pro for advanced analytics that actually help you shoot lower scores.</p>
          <div className="mt-10 grid sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
            <div className="rounded-2xl bg-white/5 border border-white/10 p-7" data-testid="landing-plan-monthly">
              <div className="label-eyebrow !text-white/60">Monthly</div>
              <div className="font-heading font-black text-5xl tracking-tighter mt-2">FJ$19<span className="text-base font-bold text-white/60">/mo</span></div>
              <div className="text-xs text-white/60 mt-1">Cancel anytime · no auto-renewal</div>
            </div>
            <div className="rounded-2xl bg-[#E05D3A] text-white p-7 relative" data-testid="landing-plan-yearly">
              <span className="absolute top-3 right-3 text-[10px] font-bold tracking-widest">SAVE FJ$59</span>
              <div className="label-eyebrow !text-white/80">Yearly</div>
              <div className="font-heading font-black text-5xl tracking-tighter mt-2">FJ$169<span className="text-base font-bold text-white/80">/yr</span></div>
              <div className="text-xs text-white/80 mt-1">Best value · works out to FJ$14/month</div>
            </div>
          </div>
          <p className="mt-6 text-xs text-white/60">7-day money-back guarantee. Secure checkout via Stripe.</p>
          <Link to="/register" className="mt-8 inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-white text-[#0F2537] font-bold hover:brightness-95 transition">
            Create your free account <ArrowRight size={16} weight="bold" />
          </Link>
        </div>
      </section>

      <footer className="border-t border-[#E2E0D8] py-10 mt-2 bg-[#F8F7F3]">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid md:grid-cols-3 gap-6 text-sm">
          <div>
            <div className="flex items-center gap-2">
              <BulaLogo size={32} rounded="rounded-lg" />
              <span className="font-heading font-black">Bula Golf</span>
            </div>
            <p className="text-[#0F2537]/60 mt-3 max-w-xs">A Fiji-focused handicap tracking, scoring and booking platform for golfers.</p>
            <p className="text-[#0F2537]/50 mt-3 max-w-xs text-xs italic">
              In honour of <strong className="text-[#0F2537]/80 not-italic">The Ebony Bear</strong> — professional Fijian golfer. The icon is his swing. 🌴
            </p>
          </div>
          <div>
            <div className="label-eyebrow">Product</div>
            <ul className="mt-3 space-y-1.5 text-[#0F2537]/80">
              <li><a href="#pricing" className="hover:text-[#E05D3A]">Pricing</a></li>
              <li><Link to="/register" className="hover:text-[#E05D3A]">Sign up free</Link></li>
              <li><Link to="/login" className="hover:text-[#E05D3A]">Sign in</Link></li>
            </ul>
          </div>
          <div>
            <div className="label-eyebrow">Company</div>
            <ul className="mt-3 space-y-1.5 text-[#0F2537]/80">
              <li><Link to="/terms" data-testid="footer-terms" className="hover:text-[#E05D3A]">Terms of Service</Link></li>
              <li><Link to="/privacy" data-testid="footer-privacy" className="hover:text-[#E05D3A]">Privacy Policy</Link></li>
              <li><a href="mailto:hello@bulagolf.app?subject=Bula%20Golf%20enquiry" data-testid="footer-contact" className="hover:text-[#E05D3A]">Contact · hello@bulagolf.app</a></li>
            </ul>
          </div>
        </div>
        <div className="text-center text-xs text-[#0F2537]/50 mt-8">
          © {new Date().getFullYear()} Bula Golf · Suva, Fiji · Built for Fiji golfers
        </div>
      </footer>
    </div>
  );
}
